// Used in assignments-list.component.html

// Angular
import { animate, style, transition, trigger } from "@angular/animations";
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatPaginator } from "@angular/material/paginator";
import { DomSanitizer } from "@angular/platform-browser";
import { Router } from "@angular/router";

// RXJS/Store
import { Observable, Subject } from "rxjs";

// Services/Types/Helpers
import { AssetAssociationsHelper, PacingHelper } from "#core/helpers";
import { AssignmentStatusHelper } from "#core/helpers/assignment-status.helper";
import { DueDateStatusHelper } from "#core/helpers/due-date-status.helper";
import { GetDiscoverActivityTypeNameHelper } from "#helpers/get-discover-activity-type.helper";
import { LanguageHelper } from "#helpers/language.helper";
import { RouteNamesHelper } from "#helpers/route-names.helper";
import { FormatDateTimeHelper } from "app/core/helpers/format-date-time.helper";
import { GetBookshelfUrlHelper } from "app/core/helpers/get-bookshelf-url.helper";
import { GetGradesHelper } from "app/core/helpers/get-grades.helper";
import { TimezoneHelper } from "app/core/helpers/timezone.helper";
import {
    AssetTypeHelper,
    PPAssignment as Assignment,
    CompositeTaskAssignment,
    Course,
    ResourceOptions,
    PPUser as User,
} from "cli-types";

// Components
import { FeedbackDialogComponent } from "#components/dialog-boxes/feedback-dialog/feedback-dialog.component";
import { AssignmentInfoDialogComponent } from "#core/components/dialog-boxes/assignment-info-dialog/assignment-info-dialog.component";
import { AssignmentHelper } from "#helpers/assignment.helper";
import { AssetId, EditionId, SubjectId } from "@carnegie-learning/common-types";
import { Asset, AssetAssociation, Bundle, CogTutorWorkspace, Edition, Perform, assetActivityTypeIds, assetTypeIds, eBookActivity, resourceTypeIds } from "@carnegie-learning/cure-types";
import { AssetFilesService } from "@carnegie-learning/data-access-cure";
import { ASSET_FILES_SERVICE } from "@carnegie-learning/data-access-angular-cure";

@Component({
    selector: "tiles",
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    templateUrl: "./tiles.component.html",
    styleUrls: ["./tiles.component.scss", "../../styles/shared-styles.scss"],
    animations: [
        trigger("fadeInOut", [
            transition(":leave", [
                // :leave is alias to '* => void'
                animate(500, style({ opacity: 0 })),
            ]),
        ]),
        trigger("collapse", [
            transition(":enter", [
                // :enter is alias to 'void => *'
                style({ height: 0, overflow: "hidden" }),
                animate(300, style({ height: "100%" })),
            ]),
            transition(":leave", [
                // :leave is alias to '* => void'
                style({ overflow: "hidden" }),
                animate(250, style({ height: 0 })),
            ]),
        ]),
    ],
    standalone: false
})
export class TilesComponent implements OnDestroy, OnInit, OnChanges {
    private ngUnsubscribe = new Subject();
    private assetFileService = inject<AssetFilesService>(ASSET_FILES_SERVICE);
    isAssignments: boolean;
    isMakeQuest: boolean;
    isCogTutor: boolean;
    public innerWidth: any;
    tileID: number;
    private IMAGE_PATH = "./assets/images/";
    embedUrl: any;
    activity: CompositeTaskAssignment;
    loading: boolean;
    selectedAssignment: Assignment;
    loadMoreAssignmentsSpinner: boolean;
    loadingMoreAssignments: boolean;
    count: number;
    fgUrl: any;
    pacingData: {
        tile: Assignment;
        pacingData: { actual?: string; pacingEstimate?: string };
    }[];
    filteredTileData: Assignment[] | Asset[] | resourceTypeIds[]; // receive assignments or resource data from parent components
    isSpineImageLoaded: boolean = false;
    s3Files: Map<string, Observable<string>> = new Map<
        string,
        Observable<string>
    >();
    readonly assetTypeIds = assetTypeIds;
    readonly assetActivityTypeIds = assetActivityTypeIds;

    @Input() tileData: Assignment[] | Asset[] | resourceTypeIds[]; // receive assignments or resource data from parent components
    @Input() selectedTile: Assignment | Asset | null; // receive current selected tile from parent components
    @Input() course: Course;
    // Resources Inputs
    @Input() public studentMetrics: any;
    @Input() public courseID: number;
    @Input() public subjectId: SubjectId;
    @Input() public editionId: EditionId;
    @Input() public subject: AssetAssociation;
    @Input() public profile: User;
    @Input() assignmentsLoaded: boolean;
    @Input() noMoreResults: boolean;
    @Input() isTeacher: boolean;
    @Input() isStudent: boolean;
    @Input() fgGridID: number;
    @Input() listView: boolean;
    @Input() gridView: boolean;
    @Input() resourceTiles: boolean;
    @Input() resourceCategories = false;
    @Input() resourceOptions: ResourceOptions[];
    @Input() allEditions: Edition[];

    @Output() loadMoreAssignments = new EventEmitter<any>();
    @Output()
    public setActiveAssignmentsEvent = new EventEmitter<Notification>();
    @Output() public setActiveResourceEvent = new EventEmitter<Notification>();
    @Output() public getFullAssignment = new EventEmitter<number>();
    @Output() public setSelectedAssignment = new EventEmitter<Assignment>();
    @Output() public setSelectedResource = new EventEmitter<Asset>();
    @Output() public setMarkedAsRead = new EventEmitter<
        Assignment | CompositeTaskAssignment
    >();
    @Output()
    public setMarkedAsReadForFeedback = new EventEmitter<Assignment>();
    @Output() setCategory = new EventEmitter<string>();

    @ViewChild(MatPaginator) paginator: MatPaginator;

    constructor(
        private assignmentHelper: AssignmentHelper,
        private assetTypeHelper: AssetTypeHelper,
        private assignmentStatusHelper: AssignmentStatusHelper,
        private router: Router,
        private getBookshelfUrl: GetBookshelfUrlHelper,
        private timezoneHelper: TimezoneHelper,
        private formatDateTimeHelper: FormatDateTimeHelper,
        private getGradesHelper: GetGradesHelper,
        public dialog: MatDialog,
        private getDiscoverActivityTypeNameHelper: GetDiscoverActivityTypeNameHelper,
        private routeNamesHelper: RouteNamesHelper,
        private domSanitizer: DomSanitizer,
        private dueDateStatusHelper: DueDateStatusHelper,
        private pacingHelper: PacingHelper,
        private assetAssociationHelper: AssetAssociationsHelper,
        private languageHelper: LanguageHelper
    ) {}

    ngOnInit() {
        // get page type
        this.isAssignments = this.onAssignmentsRoute();

        if (this.selectedTile) {
            this.tileID = this.getID(this.selectedTile); // not sure what this is used for?
            this.loadingMoreAssignments = false;
            this.loadMoreAssignmentsSpinner = false;
        } else {
            this.selectedTile = null;
        }
        this.buildPacingData(<Assignment[]>this.tileData);
        this.getOnlySharedWithStudents(<Asset[]>this.tileData);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes["tileData"] || changes["resourceOptions"]) {
            this.isAssignments = this.onAssignmentsRoute();
            this.buildPacingData(<Assignment[]>this.tileData);
            this.getOnlySharedWithStudents(<Asset[]>this.tileData);
        }
    }

    onAssignmentsRoute(): boolean {
        return this.router.url.includes("assignments");
    }

    // Get Assignment Status bar color
    getDateBarBackgroundColor(tile: Assignment | CompositeTaskAssignment) {
        return this.assignmentStatusHelper.getStatusColor(tile);
    }

    // Get Assignment Status (due, overdue, due today, In Progress, submitted, and  evaluated
    getAssignmentStatus(tile: Assignment | CompositeTaskAssignment) {
        const status = (<Assignment>tile).status;
        const dueDateStatus = this.getDueDateStatus(tile);

        if (status === "submitted") {
            return "SUBMITTED";
        } else if (status === "evaluated") {
            return "EVALUATED";
        } else if (dueDateStatus === "overdue") {
            return "OVERDUE";
        } else if (
            dueDateStatus === "dueToday" ||
            dueDateStatus === "dueThisWeek"
        ) {
            return "DUE THIS WEEK";
        } else if (status === "pending" || status === "inProgress") {
            return "UPCOMING";
        }
    }

    // Get correct Due Date Status
    getDueDateStatus(
        assignment: Assignment | CompositeTaskAssignment | undefined
    ) {
        if (assignment) {
            return this.dueDateStatusHelper.getDueDateStatus(assignment);
        }
    }
    getDueDateForDisplay(
        assignment: Assignment | CompositeTaskAssignment | undefined
    ) {
        if (assignment) {
            return this.dueDateStatusHelper.getDueDateStatusForDisplay(
                assignment
            );
        }
    }

    // get the asset type name for the SVG icon path
    getAssignableTypeForIcon(tile: Assignment | CompositeTaskAssignment) {
        if (tile && tile.asset) {
            if (tile.asset.assetActivityTypeId) {
                return this.assetTypeHelper.convertShortAssignableTypeToIconPath(
                    tile.asset.assetActivityTypeId
                );
            }

            return this.assetTypeHelper.convertShortAssignableTypeToIconPath(
                tile.asset.assetTypeId
            );
        }
    }

    // Get the capitalized asset type name
    getCapAssignableTypeName(tile: Assignment | CompositeTaskAssignment) {
        if (tile && tile.asset) {
            if (tile.asset.assetActivityTypeId) {
                return this.assetTypeHelper.getCapTypeName(
                    tile.asset.assetActivityTypeId
                );
            }

            return this.assetTypeHelper.getCapTypeName(tile.asset.assetTypeId);
        }
    }

    // Get Custom Activity Response Type
    getCustomActivityResponseType(responseType: string) {
        if (responseType) {
            return this.assignmentHelper.formatResponseType(responseType);
        }
    }

    // Open Feedback Dialog
    openFeedbackDialog(tile: Assignment | CompositeTaskAssignment) {
        if (
            tile &&
            (<Assignment>tile).feedbackUpdatedAt &&
            !(<Assignment>tile).readAt
        ) {
            this.setMarkedAsReadForFeedback.emit(<Assignment>tile);
            // Temp add a readAt date until new data is loaded
            tile = { ...tile, readAt: Date() };
            const foundIndex = this.tileData.findIndex(
                (assignment: Assignment | Asset | resourceTypeIds) =>
                    (<Assignment>assignment).assignmentID === tile.assignmentID // this typing feels clunky
            );
            (<CompositeTaskAssignment | Assignment>this.tileData[foundIndex]) =
                tile;
        }
        if (tile && tile.feedback) {
            const date = this.formatDateAndTime(
                (<Assignment>tile).feedbackUpdatedAt
            );
            const dialogRef = this.dialog.open(FeedbackDialogComponent, {
                width: "500px",
                data: { feedback: tile.feedback, feedbackUpdatedAt: date },
            });

            dialogRef.afterClosed().subscribe((result) => {
                // console.log("The dialog was closed");
            });
        }
    }

    // Get the proper name for an eBook's activity type
    getActivityTypeName(activityType: string) {
        return this.getDiscoverActivityTypeNameHelper.getDiscoverActivityTypeName(
            activityType
        );
    }

    getAssignmentName(tile: Assignment | CompositeTaskAssignment) {
        if (tile && tile.asset) {
            let name = tile.asset.displayName;
            if (
                tile.asset.assetActivityTypeId &&
                tile.asset.assetActivityTypeId === this.assetActivityTypeIds.IPA
            ) {
                name = <string>(<Bundle>tile.asset).ipaName;
            }
            return name;
        }
    }

    // Show the proper date on the front side of the card
    getProperDisplayedDate(tile: Assignment | CompositeTaskAssignment) {
        if (tile) {
            const dueDateStatus = this.getDueDateStatus(tile);
            if ((<Assignment>tile).evaluatedAt) {
                return (
                    "Evaluated: " +
                    this.formatDateAndTime((<Assignment>tile).evaluatedAt)
                );
            } else if (tile.completedAt) {
                return "Submitted: " + this.formatDateAndTime(tile.completedAt);
            } else if (dueDateStatus === "dueToday") {
                return "Due Today: " + this.formatDateAndTime(tile.dueDate);
            } else if (dueDateStatus === "dueThisWeek") {
                return "Due This Week: " + this.formatDateAndTime(tile.dueDate);
            } else if (dueDateStatus === "overdue") {
                return "Overdue: " + this.formatDateAndTime(tile.dueDate);
            } else {
                return "Due: " + this.formatDateAndTime(tile.dueDate);
            }
        }
    }
    // Get Assignment's due or submitted date
    getAssignmentDueOrSubmittedDate(
        tile: Assignment | CompositeTaskAssignment
    ) {
        if (tile) {
            if (tile.completedAt) {
                return this.formatDateAndTime(tile.completedAt);
            } else {
                return this.formatDateAndTime(tile.dueDate);
            }
        }
    }

    formatDateAndTime(date: string) {
        if (date) {
            date = this.timezoneHelper.convertServerDateToUserTZ(
                date,
                this.profile.timezone
            );
        }
        return this.formatDateTimeHelper.formatDateAndTime(date);
    }
    formatDate(date: string) {
        if (date) {
            date = this.timezoneHelper.convertServerDateToUserTZ(
                date,
                this.profile.timezone
            );
        }
        return this.formatDateTimeHelper.formatDate(date);
    }

    getGrades(assignment: Assignment) {
        return this.getGradesHelper.getGrades(assignment);
    }

    getProperActivityWord(length: number) {
        if (length === 1) {
            return "Activity";
        } else if (length > 1) {
            return "Activities";
        }
    }

    getCompletionCount(tile: Assignment | CompositeTaskAssignment) {
        if (tile && (<Assignment>tile).compositeTaskAssignments) {
            const count = 0;
            const reducer = (item: number, task: CompositeTaskAssignment) => {
                if (task.completedAt) {
                    return item + 1;
                } else {
                    return item;
                }
            };
            const c = (<Assignment>tile).compositeTaskAssignments.reduce(
                reducer,
                count
            );
            return c + "/" + (<Assignment>tile).compositeTaskAssignments.length;
        }
    }

    openTileInfoDialog(tile: Assignment | CompositeTaskAssignment) {
        let activityType = "";
        let submittedDate = "";
        let evaluatedDate = "";
        let maxAttempts: number | null = null;
        let bookName = "";
        let shareDirections = "";
        const startDate = this.formatDateAndTime(tile.startDate);
        const dueDate = this.formatDateAndTime(tile.dueDate);

        if (tile.completedAt) {
            submittedDate = this.formatDateAndTime(tile.completedAt);
        }

        if ((<Assignment>tile).evaluatedAt) {
            evaluatedDate = this.formatDateAndTime(
                (<Assignment>tile).evaluatedAt
            );
        }

        if (tile.maxAttempts) {
            maxAttempts = tile.maxAttempts;
        }

        if (tile?.asset?.assetTypeId === assetTypeIds.EBOOK_ACT) {
            activityType = this.getActivityTypeName(
                (<eBookActivity>tile.asset).bookActivityType
            );
            bookName = (<eBookActivity>tile.asset).bookName;
        }

        const dialogRef = this.dialog.open(AssignmentInfoDialogComponent, {
            width: "700px",
            panelClass: "student-assignment-info-dialog",
            data: {
                assignment: tile,
                assignmentName: this.getAssignmentName(tile),
                activityType: activityType,
                startDate: startDate,
                dueDate: dueDate,
                submittedDate: submittedDate,
                evaluatedDate: evaluatedDate,
                maxAttempts: maxAttempts,
                bookName: bookName,
                shareDirections: shareDirections,
                subjectId: this.subjectId,
                editionId: this.editionId,
                allEditions: this.allEditions,
                subject: this.subject,
                assetTypeId: (<Assignment>tile)?.asset?.assetTypeId,
                showLaunchButton: true,
            },
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result === "launch-assignment") {
                this.openAssignment(tile);
                const urlPath = this.getUrlForTile(tile);
                this.router.navigate([urlPath]);
            }
        });
        return;
    }

    // used to get the ID of the tiles
    getID(tile: Assignment | CompositeTaskAssignment | Asset) {
        if (tile && (<Assignment>tile).asset) {
            if (this.isAssignments) {
                if (
                    (<Assignment>tile)?.asset?.assetActivityTypeId ===
                    this.assetActivityTypeIds.MAKEQUEST
                ) {
                    this.isMakeQuest = true;
                }
                if (
                    (<Assignment>tile)?.asset?.assetTypeId ===
                    assetTypeIds.BUNDLE
                ) {
                    return this.getCorrectCompositeAssignmentID(
                        <Assignment>tile
                    );
                } else {
                    return (<Assignment>tile).assignmentID;
                }
            }
        } else if (this.resourceTiles) {
            return (<eBookActivity>tile).bookId;
        }
    }

    getDirectionsClipped(tile: Assignment | CompositeTaskAssignment) {
        if (tile && tile.asset) {
            let d = "",
                times: string,
                tries: string;
            if (tile.asset.assetTypeId === assetTypeIds.PERFORM) {
                const performAsset: Perform = <Perform>tile.asset,
                    timesValue: string =
                        performAsset.views < 0
                            ? "unlimited"
                            : performAsset.views.toString(),
                    triesValue: string =
                        tile.maxAttempts < 0
                            ? "unlimited"
                            : tile.maxAttempts.toString();
                if (performAsset.views > 1) {
                    times = "times";
                } else {
                    times = "time";
                }

                if (tile.maxAttempts > 1) {
                    tries = "tries";
                } else {
                    tries = "try";
                }

                d =
                    "You are about to take the activity, " +
                    (<Assignment>tile).assignmentName +
                    ". You will have " +
                    performAsset.mediaTime +
                    " seconds to view a stimulus. You can watch it up to" +
                    timesValue +
                    " " +
                    times +
                    ". Then, you will have " +
                    performAsset.recordTime +
                    " seconds to record your response. You have " +
                    triesValue +
                    " " +
                    tries +
                    " to record. Finally, you will rate your performance.";
            } else if (tile.asset.assetTypeId === assetTypeIds.BUNDLE) {
                d = (<Bundle>tile.asset).description ?? "";
            }
            return d.substr(0, 75) + "\u2026";
        }
    }

    // Get assignmentID of initial composite assignment if applicable
    getCorrectCompositeAssignmentID(
        tile: Assignment | CompositeTaskAssignment
    ) {
        if (
            <Assignment>tile &&
            (<Assignment>tile).asset &&
            <Assignment>this.selectedTile &&
            (<Assignment>this.selectedTile).asset
        ) {
            if (
                (<any>tile.asset).compositeID ===
                (<any>this.selectedTile).asset.compositeID
            ) {
                return (<any>this.selectedTile).assignmentID;
            } else {
                return tile.assignmentID;
            }
        } else if (tile) {
            return tile.assignmentID;
        }
    }

    // for ngFor
    trackByFn(index: number, item: Assignment) {
        return index; // or item.id
    }

    // check if assignment/feedback has been looked at of if there is new feedback/scores
    checkNewData(tile: Assignment | CompositeTaskAssignment) {
        // Check New Score or Feedback from Teacher
        const readAt = (<Assignment>tile).readAt;
        const feedbackUpdatedAt = (<Assignment>tile).feedbackUpdatedAt;
        const gradeEdited = (<Assignment>tile).gradeEdited;

        if (!readAt && (feedbackUpdatedAt || gradeEdited)) {
            return true;
        }
    }

    // Opens assignment
    openAssignment(tile: Assignment | CompositeTaskAssignment | Asset) {
        if (tile) {
            this.setSelectedAssignment.emit(<Assignment>tile);
        }
    }

    getUrlForTile(tile: Assignment | CompositeTaskAssignment | Asset) {
        if (tile) {
            let route: string;
            if (this.resourceTiles) {
                route = "resource";
                return this.routeNamesHelper.getRoutePath(
                    route,
                    this.courseID,
                    (<Assignment>tile).assignmentID,
                    null,
                    tile.assetId,
                    null,
                    null,
                    this.editionId || this.course?.editionId
                );
            } else if (
                (<Assignment | CompositeTaskAssignment>tile)?.asset
                    ?.assetTypeId === assetTypeIds.BUNDLE
            ) {
                route = "lesson-assignment";
            } else {
                route = "assignment";
            }

            return this.routeNamesHelper.getRoutePath(
                route,
                this.courseID,
                (<Assignment>tile).assignmentID,
                null,
                (<eBookActivity>tile).bookId ||
                    (<eBookActivity>tile).externalId,
                null,
                null,
                null
            );
        }
    }

    getUrlForNewTab(asset: eBookActivity) {
        if (!this.resourceTiles) {
            return null;
        }

        // this is a fringe case for teacher + ipad + flipgrid resource, breaks normal pattern of keeping data logic in container. Keeping it here reduces amount of api calls.
        if (this.isTeacher) {
            const resourceOption: ResourceOptions | undefined =
                this.resourceOptions?.find(
                    (opt) => opt.assetId === asset.assetId
                );
            const url = this.getBookshelfUrl.book(
                asset.bookId || asset.externalId,
                this.courseID,
                this.profile,
                resourceOption?.practice ? 1 : 0
                // spine.inQA
            );
            return url;
        }
    }

    // checks to see if assignment is a compositeAssignment
    compositeAssignment(assetTypeId: string) {
        return assetTypeId === assetTypeIds.BUNDLE;
    }

    getImageUrl(fileName: string) {
        if (!this.s3Files.has(fileName)) {
            this.s3Files.set(
                fileName,
                this.assetFileService.getFileUrl(fileName)
            );
        }
        return this.s3Files.get(fileName);
    }

    getSpineImage(panel: Assignment | CompositeTaskAssignment) {
        if (panel) {
            if ((<Assignment>panel).shortAssignableType === "Module") {
                return this.getImageUrl(
                    "images/" +
                        (<Assignment>panel).image +
                        "." +
                        (<Assignment>panel).imageExt
                );
            } else {
                return this.getImageUrl(
                    "images/" +
                        (<eBookActivity>(<Assignment>panel).asset).bookImage +
                        "-lg." +
                        (<eBookActivity>(<Assignment>panel).asset).bookImageExt
                );
            }
        }
    }

    showAssignmentImage(panel: Assignment | CompositeTaskAssignment) {
        if (
            panel &&
            ((<Assignment>panel).shortAssignableType ===
                assetTypeIds.EBOOK_ACT ||
                (<Assignment>panel).shortAssignableType ===
                    assetTypeIds.EREADER)
        ) {
            return true;
        }
    }

    // assignment/resource image
    getTileBackgroundImage(tile: Assignment | CompositeTaskAssignment) {
        if (
            tile &&
            tile.asset &&
            tile.asset.assetTypeId === assetTypeIds.COG_TUTOR
        ) {
            this.isCogTutor = true;
        }

        if (
            (tile &&
                tile.asset &&
                tile.asset.assetTypeId === assetTypeIds.BUNDLE &&
                (<Assignment>tile).image) ||
            this.resourceTiles
        ) {
            return this.getImage(tile);
        }
    }
    getTileHoverImage(tile: Assignment | CompositeTaskAssignment) {
        if (
            tile &&
            tile.asset &&
            (tile.asset.assetTypeId === assetTypeIds.EBOOK_ACT ||
                tile.asset.assetTypeId === assetTypeIds.EREADER) &&
            (<Assignment>tile).image
        ) {
            return this.getImage(tile);
        } else if (
            (tile &&
                tile.asset &&
                tile.asset.assetTypeId === assetTypeIds.BUNDLE &&
                (<Assignment>tile).image) ||
            this.resourceTiles
        ) {
            return this.getImage(tile);
        }
    }
    getImage(tile: Assignment | CompositeTaskAssignment | Asset) {
        let imageSize = "@2x.";
        if (tile && this.resourceTiles) {
            let largeSuffix = "-lg.";
            if ((<eBookActivity>tile).bookType === "crv") {
                largeSuffix = "@2x.";
            }
            return (
                "linear-gradient(to top, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0)), url(" +
                this.IMAGE_PATH +
                (<eBookActivity>tile).bookImage +
                largeSuffix +
                (<eBookActivity>tile).bookImageExt +
                ")"
            );
        } else if (tile && (<Assignment>tile).asset && this.isAssignments) {
            if (
                (<Assignment>tile)?.asset?.assetTypeId ===
                    assetTypeIds.EBOOK_ACT ||
                (<Assignment>tile)?.asset?.assetTypeId === assetTypeIds.EREADER
            ) {
                imageSize = "-lg@2x.";
            }

            if (
                (<Assignment>tile)?.asset?.assetActivityTypeId ===
                this.assetActivityTypeIds.MAKEQUEST
            ) {
                imageSize = ".";
            }

            if (
                (<Assignment>tile)?.asset?.assetTypeId === assetTypeIds.BUNDLE
            ) {
                return "linear-gradient(transparent,rgba(0,0,0,.6) 100%), url(./assets/images/rainbowRock.jpg)";
            }

            return (
                "linear-gradient(to top, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0)), url(" +
                this.IMAGE_PATH +
                (<Assignment>tile).image +
                imageSize +
                (<Assignment>tile).imageExt +
                ")"
            );
        }
    }

    setMarkedAsReadFn(event: Assignment | CompositeTaskAssignment) {
        this.setMarkedAsRead.emit(event);
    }

    buildPacingData(tileData: Assignment[]) {
        if (tileData) {
            this.pacingData = [];
            this.pacingData = tileData.map((tile: Assignment) => ({
                tile: tile,
                pacingData: this.getPacingOrActual(tile, tile.asset),
            }));
        }
    }

    getPacingOrActual(
        assignment: Assignment | CompositeTaskAssignment,
        asset: Asset | undefined
    ) {
        const actualDuration = this.getDuration(assignment);
        if (actualDuration) {
            return { actual: actualDuration };
        } else {
            if (
                asset &&
                (<Bundle>asset).tasks &&
                (<Bundle>asset).tasks.length > 0
            ) {
                const pacing = this.pacingHelper.getTotalPacing(
                    (<Bundle>asset).tasks
                );
                return {
                    pacingEstimate: pacing,
                };
            } else {
                if (asset) {
                    const pacing = this.pacingHelper.getAssetPacing(
                        <Bundle | CogTutorWorkspace>asset
                    );
                    return {
                        pacingEstimate: pacing,
                    };
                } else {
                    return { pacingEstimate: "None Set" };
                }
            }
        }
    }

    getSubjectId(asset: Asset) {
        return asset.subjectId;
    }

    formatPacing(pacingValue: number) {
        const minutes = Math.floor(pacingValue / 60);
        if (minutes <= 1) {
            return minutes + " minute";
        } else if (minutes > 1) {
            return minutes + " minutes";
        }
    }

    pacingDataEstimateAvailable(tile: Assignment | CompositeTaskAssignment) {
        const assignment = this.pacingData.filter(
            (dat) => dat.tile.assignmentID === tile.assignmentID
        )[0];
        return (
            assignment &&
            assignment.pacingData &&
            assignment.pacingData.pacingEstimate &&
            assignment.pacingData.pacingEstimate !== "None Set"
        );
    }

    pacingDataActualAvailable(tile: Assignment | CompositeTaskAssignment) {
        if (tile) {
            const firstResult = this.pacingData.filter(
                (dat) => dat.tile.assignmentID === tile.assignmentID
            )[0];
            if (!firstResult) {
                return false;
            }
            const pacingData = firstResult.pacingData;
            return (
                pacingData &&
                pacingData.actual &&
                pacingData.actual !== "None Set"
            );
        }
    }

    noPacingDataAvailable(tile: Assignment | CompositeTaskAssignment) {
        if (tile) {
            const assignment = this.pacingData.filter(
                (dat) => dat.tile.assignmentID === tile.assignmentID
            )[0];
            return (
                assignment &&
                assignment.pacingData &&
                assignment.pacingData.pacingEstimate &&
                assignment.pacingData.pacingEstimate === "None Set" &&
                !assignment.pacingData.actual
            );
        }
    }

    pacingDataActual(tile: Assignment | CompositeTaskAssignment) {
        if (tile) {
            const assignment = this.pacingData.filter(
                (dat) => dat.tile.assignmentID === tile.assignmentID
            )[0];
            return (
                assignment &&
                assignment.pacingData &&
                assignment.pacingData.actual
            );
        }
    }

    pacingDataEstimate(tile: Assignment | CompositeTaskAssignment) {
        if (tile) {
            const assignment = this.pacingData.filter(
                (dat) => dat.tile.assignmentID === tile.assignmentID
            )[0];
            return (
                assignment &&
                assignment.pacingData &&
                assignment.pacingData.pacingEstimate
            );
        }
    }

    isMathWorkspace(assignment: Assignment | CompositeTaskAssignment) {
        if (assignment && assignment.asset) {
            const subjectId = assignment.asset.subjectId;
            return (
                subjectId &&
                subjectId === "MATH" &&
                !this.isMathCustomActivity(assignment)
            );
        }
    }

    isMathCustomActivity(tile: Assignment | CompositeTaskAssignment) {
        if (
            (<Bundle>tile.asset).tasks &&
            (<Bundle>tile.asset).tasks.length > 0
        ) {
            // check if all assignments are custom activities
            return (<Bundle>tile.asset).tasks.every((task) => {
                return task && task.assetTypeId === assetTypeIds.CUSTOM_ACT;
            });
        } else {
            return (
                tile.asset &&
                tile.asset.assetTypeId &&
                tile.asset.assetTypeId === assetTypeIds.CUSTOM_ACT
            );
        }
    }

    getDuration(activity: Assignment | CompositeTaskAssignment) {
        if (activity && activity.duration) {
            const duration = activity.duration;
            return this.formatDuration(duration);
        } else {
            return null;
        }
    }

    formatDuration(duration: number) {
        if (duration > 0 && duration < 60) {
            // If duration is 1-59 seconds, display as 1 minute
            return "1 minute";
        } else {
            const minutes = Math.round(duration / 60);
            if (minutes === 1) {
                return minutes + " minute";
            } else if (minutes > 1) {
                return minutes + " minutes";
            }
        }
    }

    // Check if In Progress
    // A Bundle is In Progress if any of the activities is in the In Progress status
    checkIfInProgress(tile: Assignment | CompositeTaskAssignment) {
        if (tile && tile.asset) {
            const sat = tile.asset.assetTypeId;
            if (sat === assetTypeIds.BUNDLE) {
                // If at least 1 activity has been started and there are still un-submitted activities, show the In Progress flag
                const startedAtFound = (<Assignment>(
                    tile
                )).compositeTaskAssignments.some(
                    (activity) => activity.startedAt
                );

                const notSubmittedFound = (<Assignment>(
                    tile
                )).compositeTaskAssignments.some(
                    (activity) => !activity.completedAt
                );

                if (startedAtFound && notSubmittedFound) {
                    return true;
                }
            } else if (tile.startedAt && !tile.completedAt) {
                return true;
            }
        }
    }

    // Get Assignment Status bar color
    getPanelBodyBorder(
        panel: Assignment | CompositeTaskAssignment,
        expanded: boolean
    ) {
        if (expanded) {
            const status = (<Assignment>panel).status;
            const dueDateStatus = this.getDueDateStatus(panel);

            if (status === "submitted") {
                return status;
            } else if (status === "evaluated") {
                return status;
            } else if (dueDateStatus === "overdue") {
                return dueDateStatus;
            } else if (
                dueDateStatus === "dueToday" ||
                dueDateStatus === "dueThisWeek"
            ) {
                return "due-this-week";
            } else if (status === "pending" || status === "inProgress") {
                return "upcoming";
            }
        }
    }

    formatMaxAttempts(maxAttempts: number) {
        if (maxAttempts === -1) {
            return "unlimited tries";
        } else if (maxAttempts === 1) {
            return maxAttempts + " try";
        } else {
            return maxAttempts + " tries";
        }
    }

    getIconName(asset: Asset, panel: Assignment | undefined) {
        if (asset.assetActivityTypeId) {
            return this.assetTypeHelper.convertShortAssignableTypeToIconPath(
                asset.assetActivityTypeId
            );
        } else if (panel && panel.asset?.assetTypeId === assetTypeIds.BUNDLE) {
            const activity = (<Bundle>panel?.asset).tasks.find(
                (task) => task.assetId === asset.assetId
            );
            const taskType = activity?.assetActivityTypeId;
            if (taskType) {
                return this.assetTypeHelper.convertShortAssignableTypeToIconPath(
                    taskType
                );
            }
        }

        return this.assetTypeHelper.convertShortAssignableTypeToIconPath(
            asset.assetTypeId
        );
    }

    // Show correct due date naming
    showDueDateName(panel: Assignment | CompositeTaskAssignment) {
        if (panel) {
            if ((<Assignment>panel).dueStatus === "overdue") {
                return "Overdue";
            } else if ((<Assignment>panel).dueStatus === "dueThisWeek") {
                return "Due This Week";
            } else if ((<Assignment>panel).dueStatus === "dueToday") {
                return "Due Today";
            } else {
                return "Due Date";
            }
        }
    }

    // Retrieve proper Level name
    getLevelName(asset: Asset): string {
        const edition = this.allEditions.find(
            (e) => e.editionId === this.editionId
        );
        if (asset.levelIds && (this.subject || this.allEditions)) {
            return this.assetAssociationHelper.getAssetLevelNames(
                asset,
                this.subject,
                edition,
                this.allEditions
            );
        } else {
            return "---";
        }
    }

    multilevel(asset: Asset): boolean {
        const levelName = this.getLevelName(asset);
        return !!levelName && levelName.includes(",");
    }

    // Get the display name for a specific bundle activity
    getBundleActivityDisplayName(
        activityAssetId: AssetId,
        assignment: Assignment
    ) {
        if (activityAssetId && assignment && assignment.asset) {
            const foundActivity = (<Bundle>assignment.asset).tasks.find(
                (t) => t.assetId === activityAssetId
            );
            if (foundActivity) {
                return foundActivity.displayName;
            }
        }
    }

    getCompletedAt(activity: Asset, assignment: Assignment) {
        const activityFound = assignment.compositeTaskAssignments.find(
            (c) => c.assetId === activity.assetId
        );
        if (activityFound) {
            return activityFound.completedAt;
        }
    }
    getCategoryTileName(category: resourceTypeIds): string {
        return this.assetTypeHelper.getCapTypeName(category);
    }
    getCategoryIcon(category: resourceTypeIds): string {
        return this.assetTypeHelper.convertShortAssignableTypeToIconPath(
            category,
            true
        );
    }
    setCategoryFn(category: resourceTypeIds) {
        this.setCategory.emit(category);
    }
    getBannerClass(category: resourceTypeIds): string {
        let className = "";
        if (category) {
            className = category + "-banner";
        }
        return className;
    }
    getOnlySharedWithStudents(
        tileData: Assignment[] | Asset[] | resourceTypeIds[]
    ) {
        if (tileData && this.resourceOptions && !this.isTeacher) {
            const filteredTileData = (<Asset[]>tileData).filter((tile) => {
                let share = true;
                this.resourceOptions.forEach((resourceOption) => {
                    if (resourceOption.assetId === (<Asset>tile).assetId) {
                        share = !!resourceOption.shareWithStudents;
                    }
                });

                return share;
            });
            this.filteredTileData = filteredTileData;
        } else {
            this.filteredTileData = tileData;
        }
    }

    getCheckCircleColor(tile: Assignment | CompositeTaskAssignment) {
        if (
            tile?.dueStatus === "submittedLate" ||
            this.getDueDateStatus(tile) === "overdueButEvaluated"
        ) {
            return "overdue";
        } else if (tile?.status === "evaluated") {
            return "evaluated";
        } else {
            return "pending";
        }
    }

    getAssignmentLaunchButtonClass(tile: Assignment | CompositeTaskAssignment) {
        if (tile) {
            if (tile?.status === "pending" && tile?.dueStatus !== "overdue") {
                return "pending";
            } else if (tile?.dueStatus === "overdue") {
                return "overdue";
            } else if (
                this.checkIfInProgress(tile) &&
                tile?.dueStatus !== "overdue"
            ) {
                return "in-progress";
            } else if (tile?.completedAt && !tile?.feedback) {
                return "review";
            } else if (tile?.completedAt && tile?.feedback) {
                return "feedback";
            }
        }
    }

    getAssignmentStatusInfo(
        tile: Assignment | CompositeTaskAssignment,
        value: string
    ) {
        if (tile) {
            if (tile?.status === "pending" && tile?.dueStatus !== "overdue") {
                if (value === "status-class") {
                    return "pending";
                } else if (value === "status-tooltip") {
                    return "Assignment needs to be Submitted";
                }
            } else if (tile?.dueStatus === "overdue") {
                if (value === "status-class") {
                    return "overdue";
                } else if (value === "status-tooltip") {
                    return "Assignment is Overdue";
                }
            } else if (
                this.checkIfInProgress(tile) &&
                tile?.dueStatus !== "overdue"
            ) {
                if (value === "status-class") {
                    return "in-progress";
                } else if (value === "status-tooltip") {
                    return "Assignment In Progress";
                }
            } else if (tile?.completedAt && !tile?.evaluatedAt) {
                if (value === "status-class") {
                    return "submitted";
                } else if (value === "status-tooltip") {
                    return "Assignment has been Submitted";
                }
            } else if (tile?.completedAt && tile?.evaluatedAt) {
                if (value === "status-class") {
                    return "evaluated";
                } else if (value === "status-tooltip") {
                    return "Assignment has been Evaluated";
                }
            }
        }
    }

    isMathCourse(assignment: Assignment | CompositeTaskAssignment) {
        if (assignment && assignment.asset) {
            const subjectId = assignment.asset.subjectId;
            return this.languageHelper.ifMathLanguage(
                assignment?.asset?.subjectId
            );
        }
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(true);
        this.ngUnsubscribe.complete();
    }
}
